import { createRng } from "@/lib/seededRandom";
import type { Alert } from "@/types";
import { CLIENTS } from "./clients";
import { OPEN_ROLES } from "./employees";
import { DEPARTMENTS } from "./departments";

const rng = createRng(998877);

function clientAlerts(): Alert[] {
  const atRiskClients = CLIENTS.filter((c) => c.riskLevel === "critical" || c.riskLevel === "high").slice(0, 18);
  return atRiskClients.map((c, i) => {
    const category = rng.pick(["SLA Breach", "Escalation", "Overdue Follow-up", "Payment Overdue", "Sentiment Decline"]);
    return {
      id: `alert-client-${i}`,
      severity: c.riskLevel,
      category,
      scope: c.name,
      owner: c.assignedAM,
      issue: `${category} detected — health score ${c.healthScore}, ${c.openIssues} open issues.`,
      recommendedAction: "Schedule an immediate account review with AM and OM.",
      dueDate: rng.daysFromNow(5, 1),
      status: rng.pickWeighted<Alert["status"]>([
        ["open", 55],
        ["in-progress", 30],
        ["resolved", 10],
        ["snoozed", 5],
      ]),
      createdAt: rng.daysAgo(10),
    };
  });
}

function hiringAlerts(): Alert[] {
  return OPEN_ROLES.filter((r) => r.daysOpen > 50)
    .slice(0, 8)
    .map((r, i) => ({
      id: `alert-hiring-${i}`,
      severity: r.priority,
      category: "Hiring Bottleneck",
      scope: r.department,
      owner: r.hiringManager,
      issue: `${r.title} open for ${r.daysOpen} days with only ${r.candidatesInPipeline} candidates in pipeline.`,
      recommendedAction: "Expand sourcing channels and review compensation banding.",
      dueDate: rng.daysFromNow(10, 3),
      status: rng.pick(["open", "in-progress"] as const),
      createdAt: rng.daysAgo(12),
    }));
}

function departmentAlerts(): Alert[] {
  return DEPARTMENTS.filter((d) => d.status === "at-risk").map((d, i) => ({
    id: `alert-dept-${i}`,
    severity: "high",
    category: "Department Performance",
    scope: d.name,
    owner: d.owner,
    issue: d.aiSummary,
    recommendedAction: d.actionItems[0] ?? "Review scorecard with department owner.",
    dueDate: rng.daysFromNow(7, 2),
    status: "open",
    createdAt: rng.daysAgo(3),
  }));
}

export const ALERTS: Alert[] = [...clientAlerts(), ...hiringAlerts(), ...departmentAlerts()].sort(
  (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
);
